import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  useTheme,
  Chip,
  Stack,
  Grid,
  Paper,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  Tune as TuneIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

interface DashboardHeaderProps {
  title: string;
  subtitle?: string;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  onRefresh: () => void;
  onCustomize: () => void;
  isCustomizing?: boolean;
}

const periodOptions = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year", label: "This Year" },
];

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  subtitle,
  selectedPeriod,
  onPeriodChange,
  onRefresh,
  onCustomize,
  isCustomizing = false,
}) => {
  const theme = useTheme();
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const handlePeriodMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPeriodMenuAnchor(event.currentTarget);
  };

  const handlePeriodMenuClose = () => {
    setPeriodMenuAnchor(null);
  };

  const handlePeriodSelect = (period: string) => {
    onPeriodChange(period);
    handlePeriodMenuClose();
  };

  const getCurrentPeriodLabel = () => {
    const option = periodOptions.find((opt) => opt.value === selectedPeriod);
    return option?.label || "This Month";
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 3 },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 3 }}
      >
        {/* Left - Title Section */}
        <Grid item xs={12} md={8}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.125rem" },
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  fontWeight: 400,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Right - Controls */}
        <Grid item xs={12} md={4}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            alignItems="center"
          >
            {/* Refresh Button */}
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                width: 32,
                height: 32,
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
            {/* Period Selector */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarIcon sx={{ fontSize: "0.875rem" }} />}
              endIcon={<ArrowDownIcon sx={{ fontSize: "0.875rem" }} />}
              onClick={handlePeriodMenuOpen}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                color: "text.primary",
                borderColor: "divider",
                bgcolor: "background.paper",
                px: 1.5,
                py: 0.5,
                minWidth: 120,
                height: 32,
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              {getCurrentPeriodLabel()}
            </Button>

            <Menu
              anchorEl={periodMenuAnchor}
              open={Boolean(periodMenuAnchor)}
              onClose={handlePeriodMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  minWidth: 280,
                  maxWidth: 320,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  "& .MuiList-root": {
                    p: 1,
                  },
                },
              }}
            >
              <Grid container spacing={0.5}>
                {periodOptions.map((option) => (
                  <Grid item xs={6} key={option.value}>
                    <Paper
                      component="button"
                      onClick={() => handlePeriodSelect(option.value)}
                      sx={{
                        width: "100%",
                        p: 1.5,
                        border: "1px solid",
                        borderColor:
                          selectedPeriod === option.value
                            ? "primary.main"
                            : "divider",
                        bgcolor:
                          selectedPeriod === option.value
                            ? "primary.50"
                            : "background.paper",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 48,
                        "&:hover": {
                          bgcolor:
                            selectedPeriod === option.value
                              ? "primary.100"
                              : "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight:
                            selectedPeriod === option.value ? 600 : 500,
                          fontSize: "0.8125rem",
                          color:
                            selectedPeriod === option.value
                              ? "primary.main"
                              : "text.primary",
                        }}
                      >
                        {option.label}
                      </Typography>
                      {selectedPeriod === option.value && (
                        <CheckIcon
                          sx={{
                            fontSize: "0.875rem",
                            color: "primary.main",
                          }}
                        />
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Menu>

            {/* Customize Button */}
            <Button
              variant={isCustomizing ? "contained" : "outlined"}
              size="small"
              startIcon={<TuneIcon sx={{ fontSize: "0.875rem" }} />}
              onClick={onCustomize}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                px: 1.5,
                py: 0.5,
                height: 32,
                minWidth: 80,
                ...(isCustomizing
                  ? {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    }
                  : {
                      borderColor: "divider",
                      color: "text.primary",
                      bgcolor: "background.paper",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }),
              }}
            >
              {isCustomizing ? "Done" : "Customize"}
            </Button>

            {/* Customizing indicator */}
            {isCustomizing && (
              <Chip
                label="Drag Mode"
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor: "warning.50",
                  color: "warning.main",
                  border: "1px solid",
                  borderColor: "warning.200",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
